import React from 'react';

import { CategoryPaneStyled, ImageChooserStyled, PreviewImageStyled } from './ImageChooser.styles';

import { localization } from '../../localization';
import { ImgCollection, ImageSrc, ImgItem, OpenFileHandle, OptionsType, Settings } from '../../CommonTypes';

import { SlideContainer, AnimatedHamburgerButton, FAButton } from '../coreui';
import { Title } from '../measure_ui';
import FilePicker from '../FilePicker';
import ReactTooltip from 'react-tooltip';
import { IAppProps } from '../../App';

type InternalSrc = {
  categoryKey: string;
  imageIdx: number;
  item: ImgItem;
};

const srcID = (src: InternalSrc) => `${src.item.id}_${src.categoryKey}`;

const sameSrc = (src1: InternalSrc | undefined, src2: InternalSrc) => {
  if (src1) {
    return src1.imageIdx === src2.imageIdx && src1.categoryKey === src2.categoryKey;
  }
  return false;
}

const getRndImg = (images: ImgCollection, exclude?: InternalSrc): InternalSrc => {
  const excludeImg = exclude ? exclude.imageIdx : -1;

  let imgCount = images.images.length - (exclude ? 1 : 0);

  let imageIdx = Math.floor(Math.random() * imgCount);

  if (exclude && imageIdx >= excludeImg)
    ++imageIdx;
  
  return { imageIdx, categoryKey: images.images[imageIdx].categories[0], item: images.images[imageIdx] };
}

const categoryImages = (images: ImgCollection) => images.categories.map(c => ({
  name: c.name,
  subHeading: c.subHeading,
  key: c.key,
  images: images.images
    .map((img, i) => ({ ...img, index: i }))
    .filter(img => img.categories.includes(c.key))
}));

const getInitial = (img: ImageSrc | undefined, opts: OptionsType, images: ImgCollection, categImgs: ReturnType<typeof categoryImages>) => {
  if (img?.imageIdx !== undefined) {
    const itm = images.images[img.imageIdx];
    return {
      imageIdx: img.imageIdx,
      categoryKey: img.categoryKey !== undefined ? img.categoryKey : itm.categories[0],
      item: itm
    };
  }
  if (img !== undefined) {
    return undefined;
  }
  if (opts.id) {
    const imgIndex = images.images.findIndex(img => img.id === opts.id);
    if (imgIndex !== -1) {
      return { categoryKey: images.images[imgIndex].categories[0], imageIdx: imgIndex, item: images.images[imgIndex] }
    }
  }
  if (opts.openCat) {
    const categ = categImgs.find(c => c.key === opts.openCat);
    if (categ) {
      const imgIndex = Math.floor(Math.random() * categ.images.length);
      const item = categ.images[imgIndex];
      return { imageIdx: item.index, categoryKey: opts.openCat, item };
    }
  }
  return getRndImg(images);
};

const getInitialCat = (openCat: number | string | undefined, opts: OptionsType, activeImage: InternalSrc | undefined, categImgs: ReturnType<typeof categoryImages>) => {
  if (openCat) {
    const idx = typeof openCat === 'string' ? categImgs.findIndex(c => c.key === openCat) : openCat;
    if (idx >= 0 && idx <= categImgs.length)
      return idx;
  }
  if (activeImage) {
    const idx = categImgs.findIndex(c => c.key === activeImage.categoryKey);
    if (idx >= 0) return idx;
  }
  if (opts.allowExternalSrc)
    return categImgs.length;
  return 0;
};

const isCached = (imgSrc: string, abortLoad = false) => {
  const img = new Image();
  img.src = imgSrc;
  const cached = img.complete && img.height > 0 && img.width > 0;
  if (abortLoad)
    img.src = '';
  return cached;
};



export type ImageChooserProps = {
  props: Pick<IAppProps['props'], 'collection' | 'validExtensions'>;
  utils: IAppProps['utils'];

  settings: Settings
  allowExternalSrc: boolean;
  logoPath: string;

  currentImage?: ImageSrc;
  initialCat?: number | string;

  imageClicked?: (src: ImageSrc) => void;
  filePicked?: (file: OpenFileHandle) => void;
  requestClose?: () => void;

};

export type ImageChooserState = {
  thumbs: Map<string, string>;

  categImgs: ReturnType<typeof categoryImages>;
  activeImage?: InternalSrc;
  openCat: number;
  overlayPreview: boolean;
};
export class ImageChooser extends React.Component<ImageChooserProps, ImageChooserState> {
  private switchImageTimeout: NodeJS.Timeout | null = null;
  private loadedThumbs = new Set<number>();
  private mounted = false;

  private fillThumbs = async (cat: number) => {
    if (cat >= this.state.categImgs.length || this.loadedThumbs.has(cat))
      return;
    
    this.loadedThumbs.add(cat);
    
    for (const img of this.state.categImgs[cat].images) {
      if (!this.mounted)
        return;
      
      if (!this.state.thumbs.has(img.src)) {
        try {
          const thumb = await this.props.utils.getThumbString(img.src);
          if (this.mounted) {
            this.setState(prev => ({ thumbs: new Map(prev.thumbs).set(img.src, thumb) }));
          }
        }
        catch (err) {
          console.log(`Could not load thumbnail for ${img.id}: ${err}`);
        }
      }
    }
  };

  private intoView = (src: InternalSrc) => {
    const idx = this.state.categImgs.findIndex(c => c.key === src.categoryKey);
    if (idx >= 0) {
      let delay = 0;
      if (idx !== this.state.openCat) {
        this.setState({ openCat: idx });
        delay = this.props.settings.transitionDuration * 1000;
      }
      setTimeout(() => {
        const elem = document.getElementById(srcID(src));
        elem?.parentElement?.parentElement?.scrollTo({ top: elem.offsetTop, behavior: 'smooth' })
      }, delay);
    }
  };

  private onImageFocused = (src: InternalSrc) => {
    if (!isCached(this.props.utils.getSrcString(src.item.src))) {
      this.setState({ overlayPreview: true, activeImage: src });
    } else {
      this.setState({ activeImage: src });
    }
  };

  private randomClicked = () => {
    const src = getRndImg(this.props.props.collection, this.state.activeImage);

    const imgElem = document.getElementById(srcID(src));
    if (imgElem) {
      this.intoView(src);
      this.onImageFocused(src);
    }
  };

  private onMainImageLoad = () => {
    if (this.switchImageTimeout) {
      clearTimeout(this.switchImageTimeout);
    }
    if (this.state.overlayPreview) {
      this.switchImageTimeout = setTimeout(() => this.setState({ overlayPreview: false }), 100);
    }
  };

  private onImageClicked = (src: InternalSrc | ImageSrc) => ((e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    if (this.props.imageClicked) {
      e.preventDefault();
      this.props.imageClicked('item' in src ? {
        src: src.item.src,
        name: src.item.name,
        unit: src.item.unit,
        magnification: src.item.magnification,
        width: src.item.width,
        id: src.item.id,
        imageIdx: src.imageIdx,
        categoryKey: src.categoryKey
      } : src);
    }
  });

  private getThumb = (src: InternalSrc) => {
    return this.state.thumbs.get(src.item.src) || 'defaultThumb.jpg';
  };


  constructor(props: ImageChooserProps) {
    super(props);

    const categImgs = categoryImages(props.props.collection);
    const opts = props.utils.getDefaultOpts();
    const activeImage = getInitial(props.currentImage, opts , props.props.collection, categImgs);
    const openCat = getInitialCat(props.initialCat, opts, activeImage, categImgs);

    this.state = {
      thumbs: new Map(),
      categImgs, activeImage, openCat,
      overlayPreview: activeImage ? true : false
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fillThumbs(this.state.openCat);
    ReactTooltip.rebuild();
  }

  componentDidUpdate(prevProps: ImageChooserProps, prevState: ImageChooserState) {
    if (prevState.openCat !== this.state.openCat)
      this.fillThumbs(this.state.openCat);
  };

  componentWillUnmount() {
    this.mounted = false;
  }


  public render() {
    const {
      settings, initialCat, currentImage, allowExternalSrc, filePicked, requestClose,
      props: { validExtensions },
      utils: { getSrcString, openDialog },
    } = this.props;
    const { activeImage, overlayPreview, openCat, categImgs } = this.state;

    const catElements = categImgs.map((cat, catIndex) => ({
      heading: cat.name[settings.lang],
      content: <>
        <CategoryPaneStyled>
          {cat.subHeading ? <span className='SubHeading'>{cat.subHeading[settings.lang]}</span> : null}
          <div className='grid'>
            <div>
              {cat.images.map(img => ({ categoryKey: cat.key, imageIdx: img.index, item: img })).map(src => (
                <PreviewImageStyled key={src.imageIdx} id={srcID(src)}
                  style={{ borderColor: sameSrc(activeImage, src) ? '#156EAC' : undefined }}
                  onLoad={sameSrc(activeImage, src) && (initialCat === undefined || initialCat === catIndex)
                    ? (e => setTimeout(() => this.intoView(src), 100)) : undefined}>
                  <img
                    src={this.getThumb(src)}
                    alt=''
                    onMouseEnter={() => this.onImageFocused(src)}
                    onMouseMove={() => this.onImageFocused(src)}
                    onClick={this.onImageClicked(src)} />
                  <span>{src.item.name[settings.lang]}</span>
                </PreviewImageStyled>
              ))}
            </div>
          </div>
        </CategoryPaneStyled>
      </>
    }));

    const external = allowExternalSrc ? {
      heading: localization[settings.lang].external,
      content: <FilePicker message={localization[settings.lang].startNewMessage} allowMultiple={false} allowedFileExtensions={validExtensions}
        title={`strucTEM Measure: ${localization[settings.lang].openDialog.openImage}`} openDialog={openDialog} lang={settings.lang}
        onFilesChosen={files => { if (filePicked) filePicked(files[0]); }} />
    } : null;

    return <ImageChooserStyled>
      <div id='Description' onClick={requestClose}>
        <img id='Background' src={activeImage ? this.getThumb(activeImage) : getSrcString(currentImage!.src)} alt=''
          onClick={this.onImageClicked(activeImage || activeImage!)} />
        <img src={getSrcString(activeImage?.item.src || currentImage!.src)} alt='' style={overlayPreview ? { right: '-100%' } : {}}
          onClick={this.onImageClicked(activeImage || currentImage!)} onLoad={this.onMainImageLoad} />
        {overlayPreview ? (
          <img src={this.getThumb(activeImage!)} alt='' onClick={this.onImageClicked(activeImage!)} />
        ) : null}
        {activeImage ? <span>
          <h1>{activeImage.item.name[settings.lang]}</h1>
          {activeImage.item.description[settings.lang].split('<br/>').map((part, i) => (
            <React.Fragment key={i} >
              <br />
              {part}
            </React.Fragment>
          ))}
          {activeImage.item.magnification ? <h2>{localization[settings.lang].magnification(activeImage.item.magnification)}</h2> : null}
        </span> : null}
      </div>
      <div id='TitleShower'>
        <Title logoPath={this.props.logoPath} />
      </div>
      <div id='PreviewPane'>
        <SlideContainer slideStyle='cards-right' openElem={openCat} transitionDuration={settings.transitionDuration}
          onHeadingClick={(cat) => this.setState({ openCat: cat })}
          titleExtra={<FAButton size={35} icon={'random'} onClick={this.randomClicked} data-tip={ localization[settings.lang].tooltips.random } />}
          elements={external ? [...catElements, external] : catElements} />
      </div>
      {currentImage ? (
        <AnimatedHamburgerButton isBurger={false} onClick={requestClose} transitionDuration={settings.transitionDuration} />
      ) : null}
    </ImageChooserStyled>
  }
};